import { Injectable, computed, signal } from '@angular/core';

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  type AppLocale,
  type TranslationParams,
  type TranslationTree,
} from './locale.model';
import { TRANSLATIONS } from './translations';

function resolveBrowserLocale(): AppLocale {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE;
  }
  const lang = navigator.language?.toLowerCase() ?? '';
  if (lang.startsWith('pt')) return 'pt-BR';
  if (lang.startsWith('es')) return 'es-ES';
  if (lang.startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

function readStoredLocale(): AppLocale {
  if (typeof localStorage === 'undefined') {
    return resolveBrowserLocale();
  }
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored === 'pt-BR' || stored === 'en' || stored === 'es-ES') {
    return stored;
  }
  return resolveBrowserLocale();
}

function lookup(tree: TranslationTree | undefined, key: string): string | undefined {
  const parts = key.split('.');
  let node: string | TranslationTree | undefined = tree;
  for (const part of parts) {
    if (node == null || typeof node === 'string') {
      return undefined;
    }
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) {
    return template;
  }
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const value = params[name];
    return value == null ? '' : String(value);
  });
}

@Injectable({ providedIn: 'root' })
export class LocaleService {
  readonly locale = signal<AppLocale>(readStoredLocale());
  readonly supportedLocales = SUPPORTED_LOCALES;

  readonly currentLocaleOption = computed(
    () => SUPPORTED_LOCALES.find((l) => l.id === this.locale()) ?? SUPPORTED_LOCALES[0],
  );

  constructor() {
    this.applyDocumentLocale(this.locale());
  }

  t(key: string, params?: TranslationParams): string {
    const current = lookup(TRANSLATIONS[this.locale()], key);
    if (current != null) {
      return interpolate(current, params);
    }
    const fallback = lookup(TRANSLATIONS[DEFAULT_LOCALE], key);
    if (fallback != null) {
      return interpolate(fallback, params);
    }
    return key;
  }

  setLocale(locale: AppLocale): void {
    if (this.locale() === locale) {
      return;
    }
    this.locale.set(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    this.applyDocumentLocale(locale);
  }

  private applyDocumentLocale(locale: AppLocale): void {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }
}
